import { useState, useEffect, useCallback, FC, Fragment, ReactNode, MouseEvent } from "react";
import { useLocation, useNavigate, useParams, Params, Link } from "react-router-dom";
import { Menu, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as GridIcon } from "../../../assets/grid.icon.svg";
import { ReactComponent as TivaIcon } from "../../../assets/tivaIcon.svg";
import { ReactComponent as DriveIcon } from "../../../assets/driveIcon.svg";
import { ReactComponent as PlusCircleIcon } from "../../../assets/plus-circle.icon.svg";
import { ReactComponent as LinkIcon } from "../../../assets/link.icon.svg";
import { ReactComponent as SlidersIcon } from "../../../assets/sliders.icon.svg";
import { ReactComponent as GearIcon } from "../../../assets/gearIcon.svg";
import { ReactComponent as MoonIcon } from "../../../assets/moon.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refreshIcon.svg";
import { ReactComponent as RocketFullIcon } from "../../../assets/rocket-gradient.icon.svg";
import { ReactComponent as RocketIcon } from "../../../assets/rocket-gradient-2.icon.svg";
import { ReactComponent as CloudErrorIcon } from "../../../assets/cloudErrorIcon.svg";
import {
    forceUpdate,
    subscribeForceUpdate
} from "../../../api/admin/knowledge-base/endpoints-referrers";
import { deleteContentItemProvider } from "../../../api/admin/content-providers/endpoints-referrers";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { ForceUpdateMessage } from "../../../common/interfaces/admin/force-update-message.interface";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import { isMobileDevice } from "../../../common/utilities/platform/platform.util";
import { ThemeContainer } from "../../../common/containers/theme/theme.container";
import {
    SettingItemType,
    SettingMenuType,
    SettingItem,
    SettingMenu
} from "../../dropdown-menu/dropdown-menu.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../confirmation-popup/confirmation-popup.comp";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import LinkGoogleDriveView from "../../../views/admin/link-google-drive/link-google-drive";
import FeedbackView from "../../../views/admin/feedback/feedback";

enum MenuItemType {
    stroke = "stroke",
    fill = "fill",
    gradient = "gradient"
}

interface LinkMenuItem {
    type: MenuItemType;
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    isAdmin: boolean;
    disabled: boolean;
    link?: string;
    action?: () => void;
    settings?: SettingItem[];
}

const OptionalLink = ({
    children,
    item,
    className,
    onClick
}: {
    children: ReactNode;
    item: LinkMenuItem;
    className: string;
    onClick?: () => void;
}) => {
    const handleClick = (handler?: () => void) => {
        if (handler) {
            handler();
        }
        if (onClick) {
            onClick();
        }
    };

    return item.link && !item.disabled ? (
        <Link
            className={className}
            to={item.link}
            onClick={() => {
                handleClick(item.action);
            }}
        >
            {children}
        </Link>
    ) : (
        <a
            className={className}
            onClick={() => {
                if (!item.disabled) {
                    handleClick(item.action);
                }
            }}
        >
            {children}
        </a>
    );
};

const MenuLink = ({ item, isSelected = false }: { item: LinkMenuItem; isSelected?: boolean }) => {
    const { setLeftMenuShown } = ThemeContainer.useContainer();

    return (
        <div
            className={`general-menu-item general-menu-item--${item.type}${isSelected ? " general-menu-item--selected" : ""}${item.disabled ? " general-menu-item--disabled" : ""}`}
        >
            <OptionalLink
                item={item}
                onClick={() => {
                    setLeftMenuShown(false);
                }}
                className={`general-menu-item-link${item.settings ? " general-menu-item-link--more" : ""}`}
            >
                <SvgIcon
                    className="general-menu-item-link__icon"
                    component={item.icon}
                    inheritViewBox
                />
                <span className="general-menu-item-link__title">{item.title}</span>
            </OptionalLink>
            {item.settings && (
                <SettingMenu
                    className="general-menu-item-more"
                    type={SettingMenuType.more}
                    actions={item.settings}
                />
            )}
        </div>
    );
};

export const GeneralSubMenu = ({
    icon,
    title,
    actions,
    currentMenu
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    actions: LinkMenuItem[];
    currentMenu?: LinkMenuItem;
}) => {
    const { setLeftMenuShown } = ThemeContainer.useContainer();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = () => {
        handleMenuClose();
        setLeftMenuShown(false);
    };

    const isCurrentMenu = (item: LinkMenuItem) => {
        return !!currentMenu?.link && currentMenu.link === item.link;
    };

    return (
        <Fragment>
            <a className="general-submenu-item" onClick={handleMenuClick}>
                <SvgIcon className="general-submenu-item__icon" component={icon} inheritViewBox />
                <span className="general-submenu-item__title">{title}</span>
            </a>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "general-dropdown-menu"
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "center" }}
                anchorOrigin={{ horizontal: "right", vertical: "center" }}
            >
                {actions.map(action => (
                    <div
                        key={action.title}
                        className={`general-dropdown-menu-item${isCurrentMenu(action) ? " general-dropdown-menu-item--selected" : ""}${action.disabled ? " general-dropdown-menu-item--disabled" : ""}`}
                    >
                        <OptionalLink
                            className="general-dropdown-menu-item-link"
                            item={action}
                            onClick={handleMenuItem}
                        >
                            <SvgIcon
                                className="general-dropdown-menu-item-link__icon"
                                component={action.icon}
                                inheritViewBox
                            />
                            <span className="general-dropdown-menu-item-link__title">
                                {action.title}
                            </span>
                        </OptionalLink>
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

const GeneralMenu = ({
    assistantAvailable = true,
    collapsedType = false,
    showPromptTuning = false,
    showForceSync = false
}: {
    assistantAvailable?: boolean;
    collapsedType?: boolean;
    showPromptTuning?: boolean;
    showForceSync?: boolean;
}) => {
    const providerPathname = "knowledge-base";
    const location = useLocation();
    const navigate = useNavigate();
    const { providerId } = useParams<Params<string>>();
    const { leftMenuCollapsed, isLightTheme, changeTheme } = ThemeContainer.useContainer();
    const { providers, loadProviders } = ProvidersContainer.useContainer();
    const { user, knowledgeBases } = FirebaseAuthContainer.useContainer();
    const { isTrial } = ThemeContainer.useContainer();
    const [currentMenuItem, setCurrentMenuItem] = useState<LinkMenuItem>();
    const [mainLinkList, setMainLinkList] = useState<LinkMenuItem[]>([]);
    const [providerList, setProviderList] = useState<LinkMenuItem[]>([]);
    const [settingList, setSettingList] = useState<LinkMenuItem[]>([]);
    const [deletableProvider, setDeletableProvider] = useState<Provider>();
    const [addSourceModalOpen, setAddSourceModalOpen] = useState<boolean>(false);
    const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);
    const [deleteDriveConfirmationOpen, setDeleteDriveConfirmationOpen] = useState<boolean>(false);
    const [forceSyncConfirmationOpen, setForceSyncConfirmationOpen] = useState<boolean>(false);
    const forceSyncDescription = `Use forced synchronization only if you urgently need to update the knowledge base.
    Otherwise, automatic sync will still occur as scheduled.`;

    const handleAddSourceClick = useCallback(() => {
        if (!isMobileDevice()) {
            handleAddSourceModalOpen();
        }
    }, []);

    const handleAddSourceModalOpen = () => {
        setAddSourceModalOpen(true);
    };

    const handleAddSourceModalClose = () => {
        setAddSourceModalOpen(false);
    };

    const handleAddSourceSuccess = () => {
        loadProviders();
    };

    const handleFeedbackClick = useCallback(() => {
        if (!isMobileDevice()) {
            handleFeedbackOpen();
        }
    }, []);

    const handleFeedbackOpen = () => {
        setFeedbackOpen(true);
    };

    const handleFeedbackClose = () => {
        setFeedbackOpen(false);
    };

    const handleForceSyncClick = useCallback(() => {
        if (!isMobileDevice()) {
            handleForceSyncConfirmationOpen();
        }
    }, []);

    const handleForceSyncConfirmationOpen = () => {
        setForceSyncConfirmationOpen(true);
    };

    const handleForceSyncConfirmationClose = () => {
        setForceSyncConfirmationOpen(false);
    };

    const handleForceSyncConfirmationAction = () => {
        if (!user || !knowledgeBases.length) return;
        // TODO: get from page path params. User should choose it on Knowledge Bases page
        const knowledgeBase = knowledgeBases[0];

        forceUpdate(user.companyId, knowledgeBase.uid);
        handleForceSyncConfirmationClose();
    };

    const handleChangeTheme = useCallback(() => {
        changeTheme();
    }, [changeTheme]);

    useEffect(() => {
        setMainLinkList([
            {
                type: MenuItemType.stroke,
                icon: GridIcon,
                title: "Admin Dashboard",
                isAdmin: true,
                disabled: false,
                link: "/"
            },
            {
                type: MenuItemType.fill,
                icon: TivaIcon,
                title: "Assistant",
                isAdmin: false,
                disabled: !assistantAvailable,
                link: `${window.location.protocol}//${window.location.host.replace("admin.", "")}`
            }
        ]);
        if (isTrial) {
            setMainLinkList(prevList => [
                ...prevList,
                {
                    type: MenuItemType.gradient,
                    icon: leftMenuCollapsed ? RocketFullIcon : RocketIcon,
                    title: "Upgrade to Full Version",
                    isAdmin: true,
                    disabled: false,
                    action: handleFeedbackClick
                }
            ]);
        }
    }, [isTrial, assistantAvailable, handleFeedbackClick, leftMenuCollapsed]);

    useEffect(() => {
        setSettingList([
            {
                type: MenuItemType.stroke,
                icon: PlusCircleIcon,
                title: `Add Source${isTrial ? " (Upgrade)" : ""}`,
                isAdmin: true,
                disabled: isTrial,
                action: handleAddSourceClick
            },
            {
                type: MenuItemType.stroke,
                icon: LinkIcon,
                title: "Invite Links",
                isAdmin: true,
                disabled: false,
                link: "/manage-invites"
            },
            {
                type: MenuItemType.stroke,
                icon: MoonIcon,
                title: isLightTheme ? "Dark theme" : "Light theme",
                isAdmin: true,
                disabled: false,
                action: handleChangeTheme
            },
            {
                type: MenuItemType.stroke,
                icon: GearIcon,
                title: "Assistant Settings",
                isAdmin: true,
                disabled: false,
                link: "/assistant-settings"
            }
        ]);
        if (showPromptTuning) {
            setSettingList(prevList => [
                ...prevList,
                {
                    type: MenuItemType.stroke,
                    icon: SlidersIcon,
                    title: "Prompt Tuning",
                    isAdmin: true,
                    disabled: false,
                    link: "/prompt-tuning"
                }
            ]);
        }
        if (showForceSync) {
            setSettingList(prevList => [
                ...prevList,
                {
                    type: MenuItemType.stroke,
                    icon: RefreshIcon,
                    title: "Force Sync",
                    isAdmin: true,
                    disabled: false,
                    action: handleForceSyncClick
                }
            ]);
        }
    }, [
        isLightTheme,
        isTrial,
        showPromptTuning,
        showForceSync,
        handleForceSyncClick,
        handleAddSourceClick,
        handleChangeTheme
    ]);

    useEffect(() => {
        if (isTrial || !providers.length) return;

        setProviderList(
            providers
                .sort((a, b) => a.id - b.id)
                .map(provider => ({
                    type: MenuItemType.stroke,
                    icon: DriveIcon,
                    title:
                        provider.name === ProviderType.fileUploader
                            ? "My Files"
                            : provider.displayName,
                    isAdmin: true,
                    disabled: false,
                    hasActions: true,
                    link: `/${providerPathname}/${provider.id}`,
                    settings:
                        provider.name === ProviderType.googleDrive
                            ? [
                                  {
                                      icon: TrashIcon,
                                      name: "Delete",
                                      type: SettingItemType.default,
                                      handler: () => {
                                          setDeletableProvider(provider);
                                          setDeleteDriveConfirmationOpen(true);
                                      }
                                  }
                              ]
                            : undefined
                }))
        );
    }, [isTrial, providers]);

    useEffect(() => {
        if (!isTrial) return;

        setProviderList([
            {
                type: MenuItemType.stroke,
                icon: DriveIcon,
                title: "My Files",
                isAdmin: true,
                disabled: false,
                link: "/knowledge-base"
            },
            {
                type: MenuItemType.stroke,
                icon: DriveIcon,
                title: "Google Drive (Upgrade)",
                isAdmin: true,
                disabled: true
            },
            {
                type: MenuItemType.stroke,
                icon: DriveIcon,
                title: "One Drive (Upgrade)",
                isAdmin: true,
                disabled: true
            }
        ]);
    }, [isTrial]);

    useEffect(() => {
        if (!user) return;

        const eventSource = subscribeForceUpdate(user.companyId);

        eventSource.onopen = () => {
            console.log("Force Sync event source opend");
        };

        eventSource.onmessage = ({ data }) => {
            const message: ForceUpdateMessage = JSON.parse(data);
            if (message.commandName) {
                console.log(message);
            }
        };

        eventSource.onerror = () => {
            console.log("error when listening to Force Sync event source");
        };

        return () => {
            console.log("Force Sync event source closed");
            eventSource.close();
        };
    }, [user]);

    useEffect(() => {
        const menuItem = location.pathname.includes(providerPathname)
            ? providerList.find(item =>
                  isTrial
                      ? location.pathname === item.link
                      : `/${providerPathname}/${providerId}` === item.link
              )
            : (mainLinkList.find(item => location.pathname === item.link) ??
              settingList.find(item => location.pathname === item.link));
        setCurrentMenuItem(menuItem);
    }, [location, isTrial, providerId, providerList, mainLinkList, settingList]);

    const isCurrentMenu = (item: LinkMenuItem) => {
        return !!currentMenuItem?.link && currentMenuItem.link === item.link;
    };

    const handleDeleteDriveConfirmationAction = () => {
        if (!user || !deletableProvider) return;

        deleteContentItemProvider(user.companyId, deletableProvider.id).then(() => {
            loadProviders();

            if (Number(providerId) === deletableProvider.id) {
                navigate(`/knowledge-base`);
            }
        });
        setDeleteDriveConfirmationOpen(false);
    };

    return (
        <div className={`general-menu${collapsedType ? " general-menu--collapsed" : ""}`}>
            <div className="general-menu-items general-menu-items--fixed">
                {mainLinkList.map(item => (
                    <Fragment key={crypto.randomUUID()}>
                        <MenuLink item={item} isSelected={isCurrentMenu(item)} />
                    </Fragment>
                ))}
                {!!providerList.length && <div className="general-menu-separator"></div>}
            </div>
            <div className="general-menu-items">
                {providerList.map(item => (
                    <Fragment key={crypto.randomUUID()}>
                        <MenuLink item={item} isSelected={isCurrentMenu(item)} />
                    </Fragment>
                ))}
            </div>
            <div className="general-submenu">
                <GeneralSubMenu
                    icon={GearIcon}
                    title="Settings"
                    actions={settingList}
                    currentMenu={currentMenuItem}
                />
            </div>
            {deletableProvider && (
                <ConfirmationPopup
                    open={deleteDriveConfirmationOpen}
                    type={ConfirmationPopupType.dangerous}
                    icon={CloudErrorIcon}
                    title="Disconnect Drive"
                    description={`Disconnecting "${deletableProvider.displayName}" will remove access to all the data associated with it.
                                TIVA Assistant won't be able to answer questions on those topics.
                                This can't be undone!`}
                    actionName="Disconnect"
                    handleAction={handleDeleteDriveConfirmationAction}
                    handleClose={() => setDeleteDriveConfirmationOpen(false)}
                />
            )}
            <ConfirmationPopup
                open={forceSyncConfirmationOpen}
                icon={RefreshIcon}
                title="Force Sync"
                description={forceSyncDescription}
                actionName="Proceed"
                handleAction={handleForceSyncConfirmationAction}
                handleClose={handleForceSyncConfirmationClose}
            />
            <ModalTemplate
                open={addSourceModalOpen}
                width={654}
                handleClose={handleAddSourceModalClose}
            >
                <LinkGoogleDriveView
                    handleClose={handleAddSourceModalClose}
                    handleSuccess={handleAddSourceSuccess}
                />
            </ModalTemplate>
            <ModalTemplate
                open={feedbackOpen}
                width={654}
                height={497}
                handleClose={handleFeedbackClose}
            >
                <FeedbackView handleClose={handleFeedbackClose} />
            </ModalTemplate>
        </div>
    );
};

export default GeneralMenu;
